import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import Icon from "@/components/ui/Icon.jsx";
import { Avatar, AvatarStack, Callout, EmptyState, StatCard } from "@/components/ui/Display.jsx";
import Tag, { RoleTag } from "@/components/ui/Tag.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import TeamFormDrawer from "@/components/teams/TeamFormDrawer.jsx";
import MembersDrawer from "@/components/teams/MembersDrawer.jsx";

import { teamService, userService } from "@/lib/api.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { useAuth } from "@/context/auth.js";
import { cx } from "@/lib/cx.js";
import { idOf, memberIds, teamStats, teamTone, unassignedUsers } from "@/lib/teams.js";

/*
 * Replaces frontend/app/(dashboard)/teams/page.js.
 *
 * This page owns team membership — the Users page deliberately does not, because
 * membership lives in TWO records (User.team and Team.members) that have to move
 * together, and only the team endpoints do that.
 */

const NONE = [];

export default function Teams() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();
    const { can } = useAuth();

    const [editing, setEditing] = useState(null); // null = closed, {} = create
    const [managing, setManaging] = useState(null);
    const [formError, setFormError] = useState("");

    const [teamsQuery, usersQuery] = useQueries({
        queries: [
            {
                queryKey: ["teams"],
                queryFn: () =>
                    teamService.getOrganizationTeams().then((r) => r.data?.data?.teams ?? []),
            },
            {
                queryKey: ["users"],
                queryFn: () => userService.getAllUsers().then((r) => r.data?.data?.users ?? []),
            },
        ],
    });

    const teams = teamsQuery.data ?? NONE;
    const users = usersQuery.data ?? NONE;

    const stats = useMemo(() => teamStats(teams, users), [teams, users]);
    const unassigned = useMemo(() => unassignedUsers(users), [users]);

    // Both lists change together: moving somebody rewrites a team AND a user.
    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ["teams"] });
        queryClient.invalidateQueries({ queryKey: ["users"] });
    };

    const saveTeam = useMutation({
        mutationFn: (values) => {
            // Send only what the endpoint reads. createTeam ignores `manager`
            // (it assigns the creator) and updateTeam skips a falsy one, so an
            // empty string is noise that looks like an instruction.
            const { manager, ...rest } = values;
            const payload = manager ? { ...rest, manager } : rest;

            return editing?._id
                ? teamService.updateTeam(editing._id, payload)
                : teamService.createTeam(payload);
        },
        onSuccess: (_res, values) => {
            toast(`${values.name} ${editing?._id ? "updated" : "created"}`);
            setEditing(null);
            setFormError("");
            invalidate();
        },
        onError: (error) =>
            setFormError(apiErrorMessage(error, "Could not save this team. Please try again.")),
    });

    const saveMembers = useMutation({
        // The full list in ONE request: updateTeam diffs it server-side, so a
        // session that both adds and removes cannot half-apply.
        mutationFn: (members) => teamService.updateTeam(managing._id, { members }),
        onSuccess: (_res, members) => {
            const before = memberIds(managing).length;
            const delta = members.length - before;
            toast(
                `${managing.name}: ${members.length} ${members.length === 1 ? "member" : "members"}` +
                    (delta ? ` (${delta > 0 ? "+" : ""}${delta})` : ""),
            );
            setManaging(null);
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not update the members."), "error"),
    });

    const removeTeam = useMutation({
        mutationFn: (team) => teamService.deleteTeam(team._id),
        onSuccess: (_res, team) => {
            toast(`${team.name} deleted`, "error");
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not delete this team."), "error"),
    });

    const assignOne = useMutation({
        mutationFn: ({ team, user }) =>
            teamService.updateTeam(team._id, { members: [...memberIds(team), user._id] }),
        onSuccess: (_res, { team, user }) => {
            toast(`${user.name} added to ${team.name}`);
            invalidate();
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not assign this person."), "error"),
    });

    const askDelete = async (team) => {
        const count = memberIds(team).length;
        const ok = await confirm({
            tone: "danger",
            title: `Delete ${team.name}?`,
            body: (
                <>
                    <p>
                        The team is removed and its{" "}
                        <strong className="font-semibold text-slate-700 dark:text-slate-200">
                            {count} {count === 1 ? "member" : "members"}
                        </strong>{" "}
                        become unassigned.
                    </p>
                    <p className="mt-1.5">
                        Nobody is deleted and no tracked time is lost — but any schedule or report
                        that targets this team stops matching.
                    </p>
                </>
            ),
            okLabel: "Delete team",
        });
        if (ok) removeTeam.mutate(team);
    };

    const loading = teamsQuery.isPending || usersQuery.isPending;

    return (
        <>
            <PageChrome breadcrumb={["Teams"]} title="Teams">
                {can("manageUsers") && (
                    <Button
                        variant="primary"
                        onClick={() => {
                            setFormError("");
                            setEditing({});
                        }}
                        data-add-team
                    >
                        <Icon name="team" className="size-4" />
                        New team
                    </Button>
                )}
            </PageChrome>

            <main className="space-y-5 p-4 sm:p-5">
                <div>
                    <h1 className="text-[22px] font-semibold tracking-tight">Teams</h1>
                    <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                        Group people so schedules, reports and projects can target them.
                    </p>
                </div>

                {teamsQuery.isError && (
                    <Callout tone="danger">
                        {apiErrorMessage(teamsQuery.error, "Failed to load teams.")}{" "}
                        <button
                            type="button"
                            onClick={() => teamsQuery.refetch()}
                            className="font-semibold underline"
                        >
                            Try again
                        </button>
                    </Callout>
                )}

                <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        tone="brand"
                        label="Teams"
                        value={stats.teams}
                        hint="In this organization"
                        icon={<Icon name="team" className="size-4" />}
                    />
                    <StatCard
                        tone="emerald"
                        label="Assigned"
                        value={stats.assigned}
                        hint="People in a team"
                        icon={<Icon name="check" className="size-4" />}
                    />
                    <StatCard
                        tone="amber"
                        label="Unassigned"
                        value={stats.unassigned}
                        hint="Not in any team"
                        icon={<Icon name="userRound" className="size-4" />}
                    />
                    <StatCard
                        tone="rose"
                        label="Without a manager"
                        value={stats.withoutManager}
                        hint="Nobody can manage these"
                        icon={<Icon name="shield" className="size-4" />}
                    />
                </section>

                {/* ---- Team cards ---- */}
                {loading ? (
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-3">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                data-skeleton
                                className="h-64 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800/60"
                            />
                        ))}
                    </div>
                ) : teams.length ? (
                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-3">
                        {teams.map((team, index) => (
                            <TeamCard
                                key={team._id}
                                team={team}
                                index={index}
                                canManage={can("manageUsers")}
                                onEdit={() => {
                                    setFormError("");
                                    setEditing(team);
                                }}
                                onMembers={() => setManaging(team)}
                                onDelete={() => askDelete(team)}
                            />
                        ))}
                    </div>
                ) : (
                    <Card>
                        <CardBody>
                            <EmptyState
                                title="No teams yet"
                                hint="Teams let a schedule or a report target a group instead of naming everyone."
                                action={
                                    can("manageUsers") ? (
                                        <Button variant="primary" onClick={() => setEditing({})}>
                                            Create the first team
                                        </Button>
                                    ) : null
                                }
                            />
                        </CardBody>
                    </Card>
                )}

                {/* ---- Not in any team ---- */}
                {!loading && unassigned.length > 0 && (
                    <Card>
                        <CardHead>
                            <CardTitle
                                icon={<Icon name="userRound" className="size-4 text-amber-500" />}
                                hint="They are still tracked — but no team schedule or report includes them"
                            >
                                Not in any team
                            </CardTitle>
                            <CardExtra>
                                <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11.5px] font-semibold text-amber-700 dark:bg-amber-950/60 dark:text-amber-400">
                                    {unassigned.length} people
                                </span>
                            </CardExtra>
                        </CardHead>

                        <CardBody>
                            <ul className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 xl:grid-cols-3">
                                {unassigned.map((user) => (
                                    <li
                                        key={user._id}
                                        data-unassigned={user._id}
                                        className="flex min-w-0 items-center gap-2.5 border-b border-slate-100 py-2.5 last:border-0 dark:border-slate-800"
                                    >
                                        <Avatar name={user.name} size="sm" />
                                        <span className="min-w-0 flex-1">
                                            <Link
                                                to={`/dashboard/${user._id}`}
                                                className="block truncate text-[13px] font-medium hover:text-brand-600 dark:hover:text-brand-400"
                                            >
                                                {user.name}
                                            </Link>
                                            <span className="block truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                                                {user.email}
                                            </span>
                                        </span>

                                        {can("manageUsers") && teams.length > 0 && (
                                            <select
                                                aria-label={`Assign ${user.name} to a team`}
                                                data-assign={user._id}
                                                className="field h-7.5 w-32 py-0 text-[12px]"
                                                value=""
                                                onChange={(e) => {
                                                    const team = teams.find(
                                                        (t) => t._id === e.target.value,
                                                    );
                                                    if (team) assignOne.mutate({ team, user });
                                                }}
                                            >
                                                <option value="">Assign to…</option>
                                                {teams.map((team) => (
                                                    <option key={team._id} value={team._id}>
                                                        {team.name}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </CardBody>
                    </Card>
                )}
            </main>

            <TeamFormDrawer
                open={Boolean(editing)}
                team={editing?._id ? editing : null}
                users={users}
                onClose={() => {
                    setEditing(null);
                    setFormError("");
                }}
                onSubmit={(values) => saveTeam.mutate(values)}
                saving={saveTeam.isPending}
                error={formError}
            />

            {managing && (
                <MembersDrawer
                    key={managing._id}
                    team={managing}
                    users={users}
                    teams={teams}
                    onClose={() => setManaging(null)}
                    onSave={(members) => saveMembers.mutate(members)}
                    saving={saveMembers.isPending}
                />
            )}
        </>
    );
}

/* ---- One team ------------------------------------------------------------ */
function TeamCard({ team, index, canManage, onEdit, onMembers, onDelete }) {
    const members = team.members ?? [];
    const inactive = members.filter((m) => m.isActive === false).length;
    const manager = team.manager;

    return (
        <article
            data-team={team._id}
            className="card transition hover:-translate-y-0.5 hover:shadow-md"
        >
            <div className="card-head">
                <span
                    className={cx(
                        "grid size-9 shrink-0 place-items-center rounded-lg text-[13px] font-semibold text-white",
                        teamTone(index),
                    )}
                >
                    {team.name.slice(0, 2).toUpperCase()}
                </span>

                <div className="min-w-0">
                    <h2 className="truncate text-[15px] font-semibold tracking-tight">{team.name}</h2>
                    <p className="truncate text-[11.5px] text-slate-400 dark:text-slate-500">
                        {members.length} {members.length === 1 ? "member" : "members"}
                        {inactive ? ` · ${inactive} inactive` : ""}
                    </p>
                </div>

                {canManage && (
                    <div className="card-extra">
                        <Button variant="icon" aria-label={`Edit ${team.name}`} onClick={onEdit} data-edit={team._id}>
                            <Icon name="cog" className="size-4" />
                        </Button>
                        <Button
                            variant="icon"
                            aria-label={`Delete ${team.name}`}
                            onClick={onDelete}
                            data-delete={team._id}
                            className="text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-950/40"
                        >
                            <Icon name="xCircle" className="size-4" />
                        </Button>
                    </div>
                )}
            </div>

            <div className="card-body space-y-4">
                <p className="line-clamp-2 min-h-[2.5em] text-[13px] leading-relaxed text-slate-500 dark:text-slate-400">
                    {team.description || "No description yet."}
                </p>

                <div className="flex items-center gap-2.5">
                    <span className="w-16 shrink-0 text-[11px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                        Manager
                    </span>
                    {manager ? (
                        <span className="flex min-w-0 items-center gap-2">
                            <Avatar name={manager.name} size="xs" />
                            <Link
                                to={`/dashboard/${idOf(manager)}`}
                                className="truncate text-[13px] font-medium hover:text-brand-600 dark:hover:text-brand-400"
                            >
                                {manager.name}
                            </Link>
                            {manager.role && <RoleTag role={manager.role} />}
                        </span>
                    ) : (
                        <span className="flex items-center gap-2">
                            <Tag tone="rose">Not set</Tag>
                            {canManage && (
                                <Button variant="link" onClick={onEdit}>
                                    Assign one
                                </Button>
                            )}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2.5">
                    <span className="w-16 shrink-0 text-[11px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                        Members
                    </span>
                    {members.length ? (
                        <AvatarStack people={members} max={6} size="sm" />
                    ) : (
                        <span className="text-[13px] text-slate-400 dark:text-slate-500">
                            Nobody yet
                        </span>
                    )}

                    {canManage && (
                        <Button
                            variant="link"
                            className="ml-auto shrink-0"
                            onClick={onMembers}
                            data-members={team._id}
                        >
                            Manage
                        </Button>
                    )}
                </div>

                <Link
                    to={`/users?team=${team._id}`}
                    className="btn w-full justify-center"
                    data-view-users={team._id}
                >
                    View in Users
                </Link>
            </div>
        </article>
    );
}
